package com.eladfinish.bible.contest.screens.search.mvi

import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.extensions.formatWithCommas
import com.eladfinish.bible.contest.search_summary_format
import dev.icerock.moko.resources.format

/** What reducing a [SearchAction.SearchCompleted] yields: the next state and the event to log. */
internal data class SearchCompletion(
    val state: SearchState,
    val analyticsParams: Map<String, Any>,
)

/**
 * Reduces [action] into [state], or returns null when the action belongs to a superseded run
 * ([currentGeneration] has moved on): the current run owns isSearching and the summary.
 *
 * The counts come from the action, i.e. from the run itself. [SearchState.results] is only
 * what has been reduced so far, and a SearchResultsFound of the same run can still arrive
 * after this action.
 */
internal fun completeSearch(
    state: SearchState,
    action: SearchAction.SearchCompleted,
    currentGeneration: Int,
): SearchCompletion? {
    if (action.generation != currentGeneration) {
        return null
    }
    val wasCancelled = !state.keepRunning

    return SearchCompletion(
        state = state.copy(
            isSearching = false,
            keepRunning = true,
            regex = action.regex,
            resultsSummary = if (wasCancelled) state.resultsSummary else {
                SharedRes.strings.search_summary_format.format(
                    action.resultsCount.formatWithCommas(),
                    action.differentVersesCount.formatWithCommas(),
                )
            },
            errorMessage = null,
            dialogState = action.summaryDialog ?: SearchState.DialogState.None,
            isLoading = false,
        ),
        analyticsParams = mapOf(
            "Expression" to state.query,
            "Number of results" to action.resultsCount,
            "WasCancelled" to wasCancelled,
        ),
    )
}
